import { mergeDeepLeft } from 'ramda';
import type { Swiper } from 'swiper/react/swiper-react';
import type { SwiperModule } from 'swiper/types';

export type SwiperProps = React.ComponentProps<typeof Swiper>;

type CustomProps = SwiperProps & { modules: SwiperModule[] };

type SwiperPropsWithModule = SwiperProps & { module: SwiperModule };

export type CustomParameters = { [mode: string]: SwiperPropsWithModule };

type MergeResult = SwiperProps & {
  children?: React.ReactNode;
};

export function getCustomProps<
  S,
  C extends CustomParameters,
  P extends SwiperProps,
>(slides: S[] | undefined, customParameters: C, props: P): SwiperProps {
  if (slides == null || slides.length === 0) return {};

  const customParametersEntries = Object.entries(customParameters);
  const propsEntries = Object.entries(props);

  const customProps = propsEntries.reduce<CustomProps>(
    (acc, [keyProp]) => {
      const customParameter = customParametersEntries.find(
        ([keyCustomParameter]) => keyCustomParameter === keyProp
      );

      if (customParameter != null) {
        const [, { module, ...rest }] = customParameter;

        acc = {
          ...acc,
          ...rest,
          modules: [...acc.modules, module],
        };
      }
      return acc;
    },
    { modules: [] }
  );

  const mergedCustomProps = mergeDeepLeft<
    MergeResult,
    SwiperProps,
    MergeResult
  >(customProps, props);

  return mergedCustomProps;
}
